// How a hotspot on a captured page is tied to the element it was drawn on.
//
// A hotspot's box (x/y/w/h) is a percentage of the capture's viewport at the scroll position it was
// saved at. To keep it stuck to its content afterwards, the element under it is tagged in the page's
// HTML: `data-ih-anchor="KEY@X,Y"`. KEY is the hotspot's id; X,Y is where that element sat in the
// viewport, in CSS pixels, when the box was saved. At any later time the box is
//     saved box + (element's position now - X,Y)
// so a layout that differs from the one it was saved against (a scrollbar, different fonts, an
// element scrolled by the viewer) moves the box with its element instead of leaving it behind.
//
// One element can carry several tokens (separated by spaces): two hotspots over the same element.
// A token without "@X,Y" is the older format; the player measures its baseline on first sight.

export const ANCHOR_ATTR = "data-ih-anchor";

/**
 * How far off the frame a hotspot on a captured page may be stored, in % of the screen (100 = one screen),
 * either way. An anchored box is stored against the scroll position its page was saved at, so one the page
 * was scrolled past when it was saved sits above or below the frame, and comes back into view as the viewer
 * scrolls to it. 100 screens: far past any real page, while still a bound the server can check.
 */
export const MAX_OFF_FRAME_PCT = 10000;

/** What a hotspot id may look like to be kept as-is (and to be safe inside an attribute). */
export const HOTSPOT_ID = /^[A-Za-z0-9_-]{8,64}$/;

export interface AnchorToken {
  key: string;
  /** Anchor element's viewport position in CSS px when saved; null for the older format. */
  at: [number, number] | null;
}

const TOKEN = /^([A-Za-z0-9_-]+)(?:@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?))?$/;
const round2 = (v: number) => Math.round(v * 100) / 100;

export function parseAnchors(attr: string | null | undefined): AnchorToken[] {
  const out: AnchorToken[] = [];
  for (const raw of (attr ?? "").split(/\s+/)) {
    const m = TOKEN.exec(raw);
    if (m) out.push({ key: m[1], at: m[2] === undefined ? null : [Number(m[2]), Number(m[3])] });
  }
  return out;
}

export function formatAnchors(tokens: AnchorToken[]): string {
  return tokens.map((t) => (t.at ? `${t.key}@${round2(t.at[0])},${round2(t.at[1])}` : t.key)).join(" ");
}

/** The same tokens with their saved positions dropped: the live editor works with plain keys. */
export const plainKeys = (attr: string | null | undefined): string[] => parseAnchors(attr).map((t) => t.key);
